import React, { useState } from 'react';
import { styles } from './WalletButton.css';
import { getDetectedPlatform } from '../getDetectedPlatform';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner/Spinner';
import { ga4WalletClickTracking } from './utils';
import { CouponWalletContent } from '../../CouponModal/constants';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';

const APPLE_WALLET_URL =
  'https://cdn.shopify.com/s/files/1/0030/3235/0789/files/US-UK_Add_to_Apple_Wallet_RGB_101421_3.png';
const GOOGLE_WALLET_URL =
  'https://cdn.shopify.com/s/files/1/0030/3235/0789/files/enSG_add_to_google_wallet_add-wallet-badge_1.png';

type WalletButtonProps = {
  className?: string;
  setQrModalContent: ({ qrCode, isOpen }: { qrCode?: string; isOpen: boolean }) => void;
  getCouponWalletContent: ({
    couponCode,
    shouldGenerateQrCode,
    platform,
  }: {
    couponCode?: string;
    shouldGenerateQrCode?: boolean;
    platform?: 'ios' | 'android';
  }) => Promise<CouponWalletContent>;
  setWalletErrorCode: (code: number | null) => void;
  couponCode?: string;

  cardType: 'coupon' | 'member card';
  location?: 'modal' | 'header';
};

export const WalletButton: React.FC<WalletButtonProps> = ({
  className,
  setQrModalContent,
  getCouponWalletContent,
  setWalletErrorCode,
  couponCode,
  cardType,
  location = 'header',
}) => {
  const isDesktop = useBreakpoint('md', 'up');
  const platform = getDetectedPlatform();
  const [isLoading, setIsLoading] = useState(false);

  const onAppleClick = async () => {
    ga4WalletClickTracking({
      walletType: 'Apple',
      cardType,
      location,
    });
    setIsLoading(true);
    const couponWalletContent = await getCouponWalletContent({
      couponCode,
      shouldGenerateQrCode: isDesktop,
      platform: 'ios',
    });
    if (couponWalletContent.errorCode) {
      setWalletErrorCode(couponWalletContent.errorCode);
      return;
    }
    if (isDesktop) {
      setQrModalContent({ qrCode: couponWalletContent?.qrCode, isOpen: true });
    } else if (couponWalletContent?.appleLink) {
      window.open(couponWalletContent.appleLink, '_blank');
    }
    setIsLoading(false);
  };

  const onGoogleClick = async () => {
    ga4WalletClickTracking({
      walletType: 'Google',
      cardType,
      location,
    });
    setIsLoading(true);
    const couponWalletContent = await getCouponWalletContent({
      couponCode,
      shouldGenerateQrCode: isDesktop,
      platform: 'android',
    });
    if (couponWalletContent?.errorCode) {
      setWalletErrorCode(couponWalletContent.errorCode);
      return;
    }
    if (isDesktop) {
      setQrModalContent({ qrCode: couponWalletContent?.qrCode, isOpen: true });
    } else if (couponWalletContent?.googleLink) {
      window.open(couponWalletContent.googleLink, '_blank');
    }
    setIsLoading(false);
  };

  if (platform === 'android') {
    return (
      <button onClick={onGoogleClick} className={classNames(styles.addToWalletButton, className)}>
        {isLoading ? <Spinner /> : <img src={GOOGLE_WALLET_URL} width={160} alt="Google Wallet" />}
      </button>
    );
  }

  if (platform === 'ios') {
    return (
      <button onClick={onAppleClick} className={classNames(styles.addToWalletButton, className)}>
        {isLoading ? <Spinner /> : <img src={APPLE_WALLET_URL} width={155} alt="Apple Wallet" />}
      </button>
    );
  }

  // desktop fallback: show both
  return (
    <>
      {isLoading ? (
        <Spinner className={styles.spinner} />
      ) : (
        <button onClick={onGoogleClick} className={classNames(styles.addToWalletButton, className)}>
          <img src={GOOGLE_WALLET_URL} width={160} alt="Google Wallet" />
        </button>
      )}
      {isLoading ? (
        <Spinner className={styles.spinner} />
      ) : (
        <button onClick={onAppleClick} className={classNames(styles.addToWalletButton, className)}>
          <img src={APPLE_WALLET_URL} width={155} alt="Apple Wallet" />
        </button>
      )}
    </>
  );
};
